import Lead from "../../entities/lead";

/**
 * Lead actions
 *
 * Call order:
 *
 * LEAD_SAVE_QUEUE:	adds the item to the save queue
 * --> LEAD_SAVE:	modifies the state
 * --> LEAD_SAVE_SUCCESS: if all went well
 * --> LEAD_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail LEAD_SAVE_QUEUE is during the call to LEAD_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at LEAD_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: LEAD_SAVE_SUCCESS, LEAD_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or LEAD_PENDING_CHECK (it left the queue).
 */

export const LEAD_FETCH_ONE_QUEUE: any = 'LEAD_FETCH_ONE_QUEUE';
export const LEAD_FETCH_SUCCESS: any = 'LEAD_FETCH_SUCCESS';
export const LEAD_FETCH_FAILURE: any = 'LEAD_FETCH_FAILURE';
export const LEAD_FETCH_ONE_SUCCESS: any = 'LEAD_FETCH_ONE_SUCCESS';
export const LEAD_FETCH_ONE_FAILURE: any = 'LEAD_FETCH_ONE_FAILURE';

export const LEAD_SAVE: any = 'LEAD_SAVE';
export const LEAD_SAVE_QUEUE: any = 'LEAD_SAVE_QUEUE';
export const LEAD_SAVE_SUCCESS: any = 'LEAD_SAVE_SUCCESS';
export const LEAD_SAVE_FAILURE: any = 'LEAD_SAVE_FAILURE';

export const LEAD_SAVE_MANY: any = 'LEAD_SAVE_MANY';
export const LEAD_SAVE_MANY_QUEUE: any = 'LEAD_SAVE_MANY_QUEUE';
export const LEAD_SAVE_MANY_SUCCESS: any = 'LEAD_SAVE_MANY_SUCCESS';
export const LEAD_SAVE_MANY_FAILURE: any = 'LEAD_SAVE_MANY_FAILURE';

export const LEAD_REMOVE: any = 'LEAD_REMOVE';
export const LEAD_REMOVE_QUEUE: any = 'LEAD_REMOVE_QUEUE';
export const LEAD_REMOVE_SUCCESS: any = 'LEAD_REMOVE_SUCCESS';
export const LEAD_REMOVE_FAILURE: any = 'LEAD_REMOVE_FAILURE';

export const LEAD_RESTORE: any = 'LEAD_RESTORE';
export const LEAD_RESTORE_QUEUE: any = 'LEAD_RESTORE_QUEUE';
export const LEAD_RESTORE_SUCCESS: any = 'LEAD_RESTORE_SUCCESS';
export const LEAD_RESTORE_FAILURE: any = 'LEAD_RESTORE_FAILURE';

export const LEAD_RESTORE_MANY: any = 'LEAD_RESTORE_MANY';
export const LEAD_REMOVE_MANY: any = 'LEAD_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const LEAD_SAVE_REVERT: any = 'LEAD_SAVE_REVERT';
export const LEAD_PENDING_CHECK: any = 'LEAD_PENDING_CHECK';

/* Fetch actions */
export const fetchLeadSuccess = (leads: Lead[], partial: boolean, lastFetch: number) => ({type: LEAD_FETCH_SUCCESS, leads, partial, lastFetch});
export const fetchLeadFailure = (err: any) => {
		console.error('LeadAction: fetchLeadFailure', err);
		return {type: LEAD_FETCH_FAILURE, err};
};
export const fetchOneLeadQueue = (leadId: string, sentAddedOn?: number) => ({type: LEAD_FETCH_ONE_QUEUE, leadId, sentAddedOn});
export const fetchOneLeadSuccess = (lead: Lead | null, sentAddedOn?: number, leadId: string = lead?.id) => ({type: LEAD_FETCH_ONE_SUCCESS, leadId, lead, sentAddedOn});
export const fetchOneLeadFailure = (id: string, err: any) => {
		console.error('LeadAction: fetchOneLeadFailure', err);
		return {type: LEAD_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveLead = (lead: Lead) => {
    // console.debug('⮂ LEAD_SAVE action', lead?.id);
    return {type: LEAD_SAVE, lead};
}
export const saveLeadQueue = (lead: Lead, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ LEAD_SAVE_QUEUE action', lead?.id);
    return {type: LEAD_SAVE_QUEUE, lead, onQueued};
}
export const saveLeadSuccess = (lead: Lead, sentAddedOn?: number) => {
    // console.debug('⮂ LEAD_SAVE_SUCCESS action', lead?.id);
    return {type: LEAD_SAVE_SUCCESS, lead, sentAddedOn};
}
export const saveLeadFailure = (lead: Lead, status: number, message: string) => {
    console.warn(`⮂ LEAD_SAVE_FAILURE error saving lead: HTTP ${status}: ${message}`, lead?.id);
    return {type: LEAD_SAVE_FAILURE, lead, status, message};
};

/* SaveMany actions */
export const saveManyLeads = (leads: Lead[]) => {
    // console.debug('⮂ LEAD_SAVE_MANY action', leads?.map(lead => lead?.id).join(','));
    return {type: LEAD_SAVE_MANY, leads};
}
export const saveManyLeadsQueue = (leads: Lead[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ LEAD_SAVE_MANY_QUEUE action', leads?.map(lead => lead.id).join(','));
    return {type: LEAD_SAVE_MANY_QUEUE, leads, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `leads` (OWA-1018) */
export const saveManyLeadsSuccess = (leads: Lead[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ LEAD_SAVE_MANY_SUCCESS action', leads?.map(lead => lead.id).join(','));
    return {type: LEAD_SAVE_MANY_SUCCESS, leads, sentAddedOns};
}
export const saveManyLeadsFailure = (leads: Lead[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_LEADS_FAILURE Error saving leads: HTTP ${status}: ${message}`, leads?.map(lead => lead.id).join(','));
		return {type: LEAD_SAVE_MANY_FAILURE, leads, status, message};
};

/* Remove actions */
export const removeLead = (lead: Lead) => ({ type: LEAD_REMOVE, lead });
export const removeLeadQueue = (lead: Lead) => ({ type: LEAD_REMOVE_QUEUE, lead });
export const removeLeadSuccess = (lead: Lead) => ({ type: LEAD_REMOVE_SUCCESS, lead });
export const removeLeadFailure = (lead: Lead, status: number, message: string) => {
	console.error(`Redux: removeLeadFailure Error removing lead: HTTP ${status}: ${message}`);
	return ({ type: LEAD_REMOVE_FAILURE, lead, status, message });
};

/* Restore actions */
export const restoreLead = (lead: Lead) => ({ type: LEAD_RESTORE, lead });
export const restoreLeadQueue = (lead: Lead) => ({ type: LEAD_RESTORE_QUEUE, lead });
export const restoreLeadSuccess = (lead: Lead, sentAddedOn?: number) => ({ type: LEAD_RESTORE_SUCCESS, lead, sentAddedOn });
export const restoreLeadFailure = (lead: Lead, status: number, message: string) => {
	console.error(`Redux: restoreLeadFailure Error restoring lead: HTTP ${status}: ${message}`);
	return ({ type: LEAD_RESTORE_FAILURE, lead, status, message });
};

/* RestoreMany actions */
export const restoreManyLeads = (leads: Lead[]) => ({ type: LEAD_RESTORE_MANY, leads });

/* RemoveMany actions */
export const removeManyLeads = (leads: Lead[]) => ({ type: LEAD_REMOVE_MANY, leads });

/* Pending save actions (OWA-1018) */
/**
 * The save `lead` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertLeadSave = (lead: Lead, options: {sentAddedOn?: number, current?: Lead | null, keep?: boolean} = {}) =>
    ({type: LEAD_SAVE_REVERT, lead, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkLeadPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: LEAD_PENDING_CHECK, live, refused, readAt});